/**
 * Fonte única de verdade para todos os links de saída do NeuroEixo.
 *
 * Todo link de compra sai etiquetado (utm_source / utm_medium / utm_campaign),
 * então o relatório da Hotmart mostra de qual tela — e de qual canal — veio
 * cada assinatura. Nada de URL de compra espalhada pelo código.
 */

export const APP_URL = 'https://neuroeixovip.lovable.app';

/** Página da Hotmart que a clínica já usa (captura de e-mail + NeuroHub). */
export const HOTMART_PAGE_URL = 'https://mariliapsicologiavip.hotmart.host/neuroeixovip';
/** Link curto de compra da assinatura. */
export const HOTMART_SALES_URL = 'https://go.hotmart.com/C105887113P';
/** Portal do comprador (gerenciar assinatura / reembolso). */
export const HOTMART_MANAGE_URL = 'https://consumer.hotmart.com/';
export const HOTMART_REFUND_URL = 'https://refund.hotmart.com/';

/**
 * Canais de contato da clínica. Enquanto estiverem vazios, os botões
 * correspondentes simplesmente não aparecem.
 */
export const WHATSAPP_NUMBER = '5561999088948';
export const INSTAGRAM_HANDLE = 'mariliapsicologiavip';

const CAMPAIGN = 'neuroeixo';

export type Placement =
  | 'bio'
  | 'landing_hero'
  | 'landing_final'
  | 'landing_footer'
  | 'app_payment'
  | 'app_settings'
  | 'print_qr'
  | 'community';

type Tags = { source: string; medium: string; label: string };

const PLACEMENTS: Record<Placement, Tags> = {
  bio: { source: 'link_na_bio', medium: 'bio', label: 'Link na bio' },
  landing_hero: { source: 'site', medium: 'pagina_inicial', label: 'Página inicial' },
  landing_final: { source: 'site', medium: 'pagina_inicial_final', label: 'Página inicial (final)' },
  landing_footer: { source: 'site', medium: 'rodape', label: 'Rodapé do site' },
  app_payment: { source: 'app', medium: 'tela_de_pagamento', label: 'Tela de pagamento do app' },
  app_settings: { source: 'app', medium: 'configuracoes', label: 'Configurações do app' },
  print_qr: { source: 'clinica', medium: 'qr_impresso', label: 'QR impresso na clínica' },
  community: { source: 'site', medium: 'comunidade_gratuita', label: 'Comunidade gratuita' },
};

function tagged(base: string, tags: Record<string, string>): string {
  const url = new URL(base);
  Object.entries(tags).forEach(([key, value]) => url.searchParams.set(key, value));
  return url.toString();
}

export function placementLabel(placement: Placement): string {
  return PLACEMENTS[placement].label;
}

const ARRIVAL_KEY = 'neuroeixo:canal-chegada';

/**
 * Guarda o canal pelo qual a pessoa chegou (ex.: ?utm_source=whatsapp),
 * para que continue valendo mesmo depois que ela navega pelo app.
 */
export function captureArrivalChannel(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const source = params.get('utm_source') || params.get('ref');
  if (!source) return;
  try {
    window.localStorage.setItem(
      ARRIVAL_KEY,
      JSON.stringify({
        source,
        medium: params.get('utm_medium') || null,
        campaign: params.get('utm_campaign') || null,
        at: Date.now(),
      }),
    );
  } catch {
    // armazenamento indisponível: segue sem registrar a origem
  }
}

/** Canal de chegada, se já foi registrado em alguma visita anterior. */
export function readArrivalChannel(): string | null {
  if (typeof window === 'undefined') return null;
  const live = new URLSearchParams(window.location.search).get('utm_source');
  if (live) return live;
  try {
    const raw = window.localStorage.getItem(ARRIVAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { source?: unknown };
    return typeof parsed?.source === 'string' ? parsed.source : null;
  } catch {
    return null;
  }
}

/** Link de compra, etiquetado por tela e, quando houver, pelo canal de chegada. */
export function hotmartSalesLink(placement: Placement, channel?: string): string {
  const tags = PLACEMENTS[placement];
  return tagged(HOTMART_SALES_URL, {
    utm_source: channel || tags.source,
    utm_medium: tags.medium,
    utm_campaign: CAMPAIGN,
  });
}

/** Link da página da Hotmart (captura / comunidade), também etiquetado. */
export function hotmartPageLink(placement: Placement, channel?: string): string {
  const tags = PLACEMENTS[placement];
  return tagged(HOTMART_PAGE_URL, {
    utm_source: channel || tags.source,
    utm_medium: tags.medium,
    utm_campaign: CAMPAIGN,
  });
}

/** Endereço do app, etiquetado quando vem de material impresso. */
export function appLink(path = '', placement?: Placement): string {
  const base = `${APP_URL}${path}`;
  if (!placement) return base;
  const tags = PLACEMENTS[placement];
  return tagged(base, {
    utm_source: tags.source,
    utm_medium: tags.medium,
    utm_campaign: CAMPAIGN,
  });
}

export function whatsappLink(message?: string): string | null {
  const digits = WHATSAPP_NUMBER.replace(/\D/g, '');
  if (!digits) return null;
  const url = new URL(`https://wa.me/${digits}`);
  if (message) url.searchParams.set('text', message);
  return url.toString();
}

export function instagramLink(): string | null {
  const handle = INSTAGRAM_HANDLE.trim().replace(/^@/, '');
  if (!handle) return null;
  return `https://instagram.com/${handle}`;
}
